/**
 * Simulation event bus.
 *
 * The simulation never touches the renderer. It emits typed events, and the
 * client subscribes to whichever ones it wants to visualise. This is the only
 * outward channel from `src/sim` other than state snapshots, and it is what
 * keeps the core engine-agnostic.
 */

export type SimEvent =
  | { type: 'day.started'; day: number; dayOfWeek: string }
  | { type: 'day.ended'; day: number; summary: DaySummary }
  | { type: 'money.changed'; delta: number; balanceCash: number; reason: string }
  | { type: 'expense.charged'; name: string; amount: number; paid: boolean }
  | { type: 'job.offered'; jobId: string; title: string }
  | { type: 'job.started'; jobId: string; title: string }
  | { type: 'job.completed'; jobId: string; title: string; pay: number; quality: number }
  | { type: 'job.failed'; jobId: string; title: string; reason: string }
  | { type: 'lead.generated'; leadId: string; customerName: string; payout: number }
  | { type: 'lead.expired'; leadId: string; customerName: string }
  | { type: 'contract.signed'; customerName: string; weeklyValue: number }
  | { type: 'contract.lost'; customerName: string; reason: string }
  | { type: 'reputation.changed'; delta: number; value: number }
  | { type: 'equipment.purchased'; itemId: string; name: string; price: number }
  | { type: 'equipment.serviced'; itemId: string; name: string; cost: number }
  | { type: 'equipment.worn'; itemId: string; name: string; condition: number }
  | { type: 'employee.hired'; employeeId: string; name: string; wage: number }
  | { type: 'employee.fired'; employeeId: string; name: string }
  | { type: 'employee.completedJob'; employeeId: string; name: string; pay: number }
  | { type: 'employee.paid'; employeeId: string; name: string; amount: number }
  | { type: 'housing.upgraded'; name: string }
  | { type: 'vehicle.purchased'; name: string }
  | { type: 'debt.taken'; amount: number; source: string }
  | { type: 'debt.repaid'; amount: number; remaining: number }
  | { type: 'milestone.reached'; id: string; label: string }
  | { type: 'need.critical'; need: string }
  | { type: 'notice'; level: 'info' | 'warn' | 'bad' | 'good'; message: string };

export interface DaySummary {
  day: number;
  earned: number;
  spent: number;
  net: number;
  jobsDone: number;
  employeeJobsDone: number;
  cashEnd: number;
  netWorthEnd: number;
  reputation: number;
}

export type SimEventType = SimEvent['type'];
export type Listener = (event: SimEvent) => void;

export class EventBus {
  private listeners = new Set<Listener>();
  /** Events emitted this tick, useful for tests and for the day summary. */
  private log: SimEvent[] = [];
  private logLimit = 500;

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit(event: SimEvent): void {
    this.log.push(event);
    if (this.log.length > this.logLimit) this.log.shift();
    for (const listener of this.listeners) listener(event);
  }

  /** Recent events, newest last. Not serialised into saves. */
  recent(count = 50): SimEvent[] {
    return this.log.slice(-count);
  }

  clearLog(): void {
    this.log = [];
  }
}
